import React, { useEffect, useMemo, useState } from "react";
import {
  SideSheet, Button, Input, Select, Space, Toast, Tabs, TabPane, Typography,
} from "@douyinfe/semi-ui";
import { IconSave, IconUpload, IconCopy } from "@douyinfe/semi-icons";
import CodeMirror from "@uiw/react-codemirror";
import { html as htmlLang } from "@codemirror/lang-html";
import type { EditorView } from "@codemirror/view";
import { ManualApi, Manual, ManualCategory, ManualSaveRequest, ManualAsset } from "./api";
import { SnippetToolbar } from "./SnippetToolbar";
import { ManualVersionDrawer } from "./ManualVersionDrawer";
import { AigcModal } from "./AigcModal";

const { Text } = Typography;

interface Props {
  visible: boolean;
  manual: Manual | null;
  categories: ManualCategory[];
  onClose: () => void;
}

export function ManualEditor({ visible, manual, categories, onClose }: Props) {
  const [title, setTitle] = useState("");
  const [slug, setSlug] = useState("");
  const [summary, setSummary] = useState("");
  const [categoryId, setCategoryId] = useState<number | undefined>(undefined);
  const [htmlContent, setHtmlContent] = useState("");
  const [saving, setSaving] = useState(false);
  const [versionDrawerVisible, setVersionDrawerVisible] = useState(false);
  const [aigcVisible, setAigcVisible] = useState(false);

  const [assets, setAssets] = useState<ManualAsset[]>([]);
  const [uploading, setUploading] = useState(false);
  const [activeTab, setActiveTab] = useState<"preview" | "assets">("preview");

  const editorRef = React.useRef<EditorView | null>(null);

  const refreshAssets = async () => {
    if (manual?.id) {
      try {
        const list = await ManualApi.listAssets(manual.id);
        setAssets(list);
      } catch (e: any) {
        Toast.error("加载图片失败：" + (e?.message || ""));
      }
    }
  };

  useEffect(() => {
    if (visible && manual?.id && activeTab === "assets") {
      refreshAssets();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, manual?.id, activeTab]);

  const onUpload = async (files: File[]) => {
    if (!manual?.id) {
      Toast.warning("请先保存手册");
      return;
    }
    if (!files.length) return;
    setUploading(true);
    try {
      const created = await ManualApi.uploadAssetsBatch(manual.id, files);
      Toast.success(
        `已上传 ${created.length} 张，分配为 [IMG-${created[0].assetNo}] ~ [IMG-${created[created.length - 1].assetNo}]`
      );
      await refreshAssets();
    } catch (e: any) {
      Toast.error("上传失败：" + (e?.message || ""));
    } finally {
      setUploading(false);
    }
  };

  const onDeleteAsset = async (assetNo: number) => {
    if (!manual?.id) return;
    try {
      await ManualApi.deleteAsset(manual.id, assetNo);
      await refreshAssets();
      Toast.info(`已删除 [IMG-${assetNo}]`);
    } catch (e: any) {
      Toast.error("删除失败：" + (e?.message || ""));
    }
  };

  const onCopyPlaceholder = async (assetNo: number) => {
    try {
      await navigator.clipboard.writeText(`[IMG-${assetNo}]`);
      Toast.success(`已复制 [IMG-${assetNo}]`);
    } catch {
      Toast.warning("剪贴板不可用，请手动输入");
    }
  };

  useEffect(() => {
    if (visible) {
      setTitle(manual?.title || "");
      setSlug(manual?.slug || "");
      setSummary(manual?.summary || "");
      setCategoryId(manual?.categoryId || categories[0]?.id);
      setHtmlContent(manual?.htmlContent || "<h1>新手册</h1><p>在这里编写内容…</p>");
    }
  }, [visible, manual, categories]);

  const previewDoc = useMemo(() => {
    return `<!DOCTYPE html>
<html lang="zh-CN">
<head>
  <meta charset="UTF-8">
  <link href="/api/mis/manual/theme.css" rel="stylesheet">
  <script src="/static/manual/theme/vendor/mermaid.min.js" defer></script>
</head>
<body>
${htmlContent}
<script>window.addEventListener('load',function(){if(window.mermaid)mermaid.initialize({startOnLoad:true,theme:'forest'});});</script>
</body>
</html>`;
  }, [htmlContent]);

  const onSave = async () => {
    if (!title.trim()) { Toast.warning("请输入标题"); return; }
    if (!slug.trim()) { Toast.warning("请输入路由"); return; }
    if (!categoryId) { Toast.warning("请选择分类"); return; }
    if (!htmlContent.trim()) { Toast.warning("内容不能为空"); return; }

    setSaving(true);
    try {
      const data: ManualSaveRequest = {
        slug: slug || undefined,
        title, summary, categoryId, htmlContent,
      };
      if (manual) {
        await ManualApi.update(manual.id, data);
        Toast.success("已保存");
      } else {
        await ManualApi.create(data);
        Toast.success("已创建");
      }
      onClose();
    } catch (e: any) {
      Toast.error("保存失败：" + (e?.message || ""));
    } finally {
      setSaving(false);
    }
  };

  return (
    <SideSheet
      title={manual ? "编辑手册" : "新建手册"}
      visible={visible}
      onCancel={onClose}
      width="90%"
      bodyStyle={{ padding: 0, height: "calc(100vh - 56px)" }}
    >
      <div style={{ padding: 12, borderBottom: "1px solid #eee", display: "flex", gap: 12, alignItems: "center" }}>
        <Input value={title} onChange={setTitle} placeholder="标题" style={{ width: 200 }} />
        <Input value={slug} onChange={setSlug} placeholder="slug（必填，路由寻址用）" style={{ width: 220 }} />
        <Select value={categoryId} onChange={setCategoryId} style={{ width: 160 }}
                optionList={categories.map(c => ({ value: c.id, label: c.name }))} />
        <Input value={summary} onChange={setSummary} placeholder="摘要（可选）" style={{ flex: 1 }} />
        <Space>
          <Button onClick={() => setAigcVisible(true)}>✨ AI 生成</Button>
          {manual && (
            <Button onClick={() => setVersionDrawerVisible(true)}>历史版本</Button>
          )}
          <Button icon={<IconSave />} theme="solid" type="primary" loading={saving}
                  onClick={onSave}>保存</Button>
        </Space>
      </div>

      <div
        style={{
          padding: "8px 12px",
          borderBottom: "1px solid #eee",
          background: "#fafafa",
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
        }}
      >
        <SnippetToolbar editorRef={editorRef} />
      </div>
      <div style={{ display: "flex", height: "calc(100vh - 156px)" }}>
        <div style={{ flex: 1, borderRight: "1px solid #eee", overflow: "" }}>
          <CodeMirror
            value={htmlContent}
            height="calc(100vh - 156px)"
            theme="light"
            extensions={[htmlLang()]}
            onChange={(val) => setHtmlContent(val)}
            onCreateEditor={(view) => {
              editorRef.current = view;
            }}
          />
        </div>
        <div style={{ flex: 1, overflow: "auto" }}>
          {manual?.id ? (
            <Tabs activeKey={activeTab} onChange={k => setActiveTab(k as any)}>
              <TabPane tab="预览" itemKey="preview">
                <iframe
                  srcDoc={previewDoc}
                  style={{ width: "100%", height: "calc(100vh - 260px)", border: "none" }}
                  title="预览"
                  sandbox="allow-scripts allow-same-origin"
                />
              </TabPane>
              <TabPane tab={`图片 (${assets.length})`} itemKey="assets">
                <div style={{ padding: 12 }}>
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    style={{ display: "none" }}
                    ref={(el) => {
                      // store for click trigger
                      (window as any).__manualImgInput = el;
                    }}
                    onChange={(e) => {
                      const files = Array.from(e.target.files || []);
                      if (files.length) onUpload(files);
                      e.target.value = "";
                    }}
                  />
                  <Button
                    icon={<IconUpload />}
                    theme="solid"
                    type="primary"
                    loading={uploading}
                    onClick={() => (window as any).__manualImgInput?.click()}
                  >
                    批量上传图片（自动分配 [IMG-n]）
                  </Button>
                  <Text type="tertiary" size="small" style={{ display: "block", marginTop: 8 }}>
                    提示：可一次选择多张；后端按顺序分配序号。点击"复制"按钮拿到占位符。
                  </Text>

                  <div style={{ marginTop: 16, display: "flex", flexDirection: "column", gap: 8 }}>
                    {assets.length === 0 && (
                      <Text type="tertiary">暂无图片资产</Text>
                    )}
                    {assets.map(a => (
                      <div key={a.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: 8, border: "1px solid #eee", borderRadius: 6 }}>
                        <img src={a.ossUrl} alt={a.originalFilename} style={{ width: 64, height: 64, objectFit: "cover", borderRadius: 4 }} />
                        <div style={{ flex: 1 }}>
                          <div><strong>[IMG-{a.assetNo}]</strong> · {a.originalFilename}</div>
                          <Text type="tertiary" size="small">{Math.round((a.sizeBytes || 0) / 1024)} KB · {a.contentType}</Text>
                        </div>
                        <Button size="small" icon={<IconCopy />} onClick={() => onCopyPlaceholder(a.assetNo)}>复制占位符</Button>
                        <Button size="small" type="danger" onClick={() => onDeleteAsset(a.assetNo)}>删除</Button>
                      </div>
                    ))}
                  </div>
                </div>
              </TabPane>
            </Tabs>
          ) : (
            <>
              <div style={{ padding: 24, textAlign: "center", color: "#999" }}>
                保存手册后即可上传图片
              </div>
              <iframe
                srcDoc={previewDoc}
                style={{ width: "100%", height: "calc(100vh - 260px)", border: "none" }}
                title="预览"
                sandbox="allow-scripts allow-same-origin"
              />
            </>
          )}
        </div>
      </div>

      <ManualVersionDrawer
        visible={versionDrawerVisible}
        manualId={manual?.id || null}
        onClose={() => setVersionDrawerVisible(false)}
        onRollbackDone={async () => {
          if (manual) {
            const updated = await ManualApi.get(manual.id);
            setTitle(updated.title);
            setSlug(updated.slug || "");
            setSummary(updated.summary || "");
            setCategoryId(updated.categoryId);
            setHtmlContent(updated.htmlContent);
            Toast.info("已回滚，编辑器已刷新");
          }
        }}
      />
      <AigcModal
        visible={aigcVisible}
        onApply={(generated) => {
          setHtmlContent(generated);
          setAigcVisible(false);
          Toast.success("已应用到编辑器");
        }}
        onClose={() => setAigcVisible(false)}
      />
    </SideSheet>
  );
}
